import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'

import { GeckoComponentCoords } from './geckoComponentCoords'
import { GeckoComponentRect } from './geckoComponentRect'

export enum LineDirectionX {
  Right,
  Left,
  None,
}

export enum LineDirectionY {
  Up,
  Down,
  None,
}

const wrapTypeValues = ['x', 'y'] as const
export type WrapType = (typeof wrapTypeValues)[number]

export function isWrapType(value: string): value is WrapType {
  return wrapTypeValues.includes(value as WrapType)
}

function coerceToFloat(val: string | number): number {
  return typeof val === 'string' ? parseFloat(val) : val
}

/**
 * A class representing an ordered series of points on the surface of an asset,
 * most frequently used for markup rendering of polygons or lines.
 */
export class GeckoComponentPath {
  //----------------------------------------------------------------------------

  /** The points traced by this path */
  public readonly points: ReadonlyArray<GeckoComponentCoords>
  public readonly pathDirectionX: ReadonlyArray<LineDirectionX>
  public readonly pathDirectionY: ReadonlyArray<LineDirectionY>

  /** The bounds of this path (derived from the minimum and maximum coordinates) */
  public readonly bounds: GeckoComponentRect

  /**
   * A simple constructor- private to encourage use of more descriptive static
   * factory methods like CreateFromValues()
   * @param points The points traced by this path
   * @param pathDirectionX The x direction of each line segment
   * @param pathDirectionY The y direction of each line segment
   */
  constructor(
    points: ReadonlyArray<GeckoComponentCoords>,
    pathDirectionX: ReadonlyArray<LineDirectionX> | undefined = undefined,
    pathDirectionY: ReadonlyArray<LineDirectionY> | undefined = undefined
  ) {
    const firstPoint = points[0]
    let minX = Infinity
    let minY = Infinity
    let maxX = -Infinity
    let maxY = -Infinity
    console.assert(!!firstPoint, 'ComponentPaths must have at least one point!')
    const projectedPoints = []
    const derivedDirectionX = []
    const derivedDirectionY = []

    for (let i = 0; i < points.length; i++) {
      const point = points[i]
      const followingPoint = points.at(i + 1)
      console.assert(
        point.componentID === firstPoint.componentID,
        'ComponentPaths cannot include coordinates on different components!'
      )
      if (isFinite(point.x.num)) {
        minX = Math.min(minX, point.x.num)
        maxX = Math.max(maxX, point.x.num)
      } else {
        continue
      }

      if (isFinite(point.y.num)) {
        minY = Math.min(minY, point.y.num)
        maxY = Math.max(maxY, point.y.num)
      } else {
        continue
      }

      if (
        followingPoint !== undefined &&
        (pathDirectionX === undefined || pathDirectionY === undefined)
      ) {
        derivedDirectionX.push(
          determineXDirection(
            point.x.toNumber(point.x.unit),
            followingPoint.x.toNumber(point.x.unit)
          )
        )
        derivedDirectionY.push(
          determineYDirection(
            point.y.toNumber(point.y.unit),
            followingPoint.y.toNumber(point.y.unit)
          )
        )
      }

      projectedPoints.push(point.project(firstPoint.space))
    }

    if (pathDirectionX !== undefined && pathDirectionY !== undefined) {
      this.pathDirectionX = pathDirectionX
      this.pathDirectionY = pathDirectionY
    } else {
      this.pathDirectionX = derivedDirectionX
      this.pathDirectionY = derivedDirectionY
    }

    this.points = projectedPoints

    this.bounds = GeckoComponentRect.CreateFromXYs(
      firstPoint.componentID,
      minX,
      minY,
      maxX,
      maxY,
      firstPoint.x.unit,
      firstPoint.y.unit
    )
  }

  /**
   * An accessor method for this object's component ID (an accessor rather than a
   * variable since this is already stored on both the start and end coordinates)
   */
  public get componentID() {
    return this.bounds.componentID
  }

  /**
   * An accessor method for units of the 2 dimensional space that this rect
   * exists in.
   */
  public get space() {
    return this.bounds.space
  }

  /** An accessor method for this object's x range */
  public get x() {
    return this.bounds.x
  }

  /** An accessor method for this object's y range */
  public get y() {
    return this.bounds.y
  }

  //----------------------------------------------------------------------------

  /**
   * A factory method for creating a GeckoComponentPath from an array of [x, y]
   * coordinates on a given component's geometry.
   * @param componentID   The ID of the component this rect is on
   * @param coords        An array of [x, y] coordinates on a component
   * @param xUnit         The new measurement unit of the x coordinate
   * @param yUnit         The new measurement unit of the y coordinate
   * @param pathDiectionX The x direction of each line segment
   * @param pathDiectionY The y direction of each line segment
   * @constructor
   */
  public static CreateFromValues(
    componentID: string,
    coords: ReadonlyArray<{ x: number | string; y: number | string }>,
    xUnit: GeckoMeasurementUnit,
    yUnit: GeckoMeasurementUnit,
    pathDiectionX: ReadonlyArray<LineDirectionX> | undefined = undefined,
    pathDiectionY: ReadonlyArray<LineDirectionY> | undefined = undefined
  ): GeckoComponentPath {
    return new GeckoComponentPath(
      coords.map(({ x, y }) => {
        const xNum =
          x === 'Infinity'
            ? Infinity
            : x === '-Infinity'
              ? -Infinity
              : Number(x)
        const yNum =
          y === 'Infinity'
            ? Infinity
            : y === '-Infinity'
              ? -Infinity
              : Number(y)
        return GeckoComponentCoords.CreateFromValues(
          componentID,
          xNum,
          xUnit,
          yNum,
          yUnit
        )
      }),
      pathDiectionX,
      pathDiectionY
    )
  }

  /**
   * A factory method for creating a path based on markup, with an optional array of 'wraps'
   * to denote the patch crossing the axis in the 'x' or 'y' direction.
   * This calls through to 'CreateFromValues', with 'direction' arrays computed from the
   * wraps, if supplied.
   *
   * The length of the 'wraps' array must be 1 less than the coords array, and the index
   * denotes which edge each 'wraps' applies to
   *
   * @param componentID   The ID of the component this rect is on
   * @param coords        An array of [x, y] coordinates on a component
   * @param xUnit         The new measurement unit of the x coordinate
   * @param yUnit         The new measurement unit of the y coordinate
   * @param wraps         An array of arrays, element type ('x' || 'y')[] to denote wrapping in the 'x' or 'y' direction
   * @constructor
   */
  public static CreateFromPointsAndWraps(
    componentID: string,
    coords: ReadonlyArray<{ x: number | string; y: number | string }>,
    xUnit: GeckoMeasurementUnit,
    yUnit: GeckoMeasurementUnit,
    wraps: ReadonlyArray<ReadonlySet<WrapType>> | undefined
  ): GeckoComponentPath {
    if (wraps && wraps.length !== coords.length - 1) {
      throw new Error(
        `Bad wraps count. Expected ${coords.length - 1} but got ${wraps.length}`
      )
    }

    const pathDirectionsX: ReadonlyArray<LineDirectionX> | undefined = wraps
      ? coords.slice(0, coords.length - 1).map((coord, idx) => {
          const coordNext = coords[idx + 1]
          const [x_start, x_end] = [coord.x, coordNext.x].map((num) =>
            coerceToFloat(num)
          )
          return determineXDirection(x_start, x_end, !wraps[idx].has('x'), true)
        })
      : undefined
    const pathDirectionsY: ReadonlyArray<LineDirectionY> | undefined = wraps
      ? coords.slice(0, coords.length - 1).map((coord, idx) => {
          const coordNext = coords[idx + 1]
          const [y_start, y_end] = [coord.y, coordNext.y].map((num) =>
            coerceToFloat(num)
          )
          return determineYDirection(y_start, y_end, !wraps[idx].has('y'), true)
        })
      : undefined

    return this.CreateFromValues(
      componentID,
      coords,
      xUnit,
      yUnit,
      pathDirectionsX,
      pathDirectionsY
    )
  }

  /**
   * A factory method for creating a GeckoComponentPath from a horizontal line (y_start === y_end).
   * A horizontal line will wrap if (x_start > x_end).
   * @param lineGeometry        The rect representing line geometry
   * @constructor
   */
  public static CreateFromHorizontalLine(
    lineGeometry: GeckoComponentRect
  ): GeckoComponentPath {
    const x_start = lineGeometry.x.start.num
    const y = lineGeometry.y.start.num
    const x_end = lineGeometry.x.end.num
    return GeckoComponentPath.CreateFromValues(
      lineGeometry.componentID,
      [
        { x: x_start, y: y },
        { x: x_end, y: y },
      ],
      lineGeometry.x.unit,
      lineGeometry.y.unit,
      [LineDirectionX.Right],
      [LineDirectionY.None]
    )
  }

  /**
   * A factory method for creating a GeckoComponentPath from a vertical line (x_start === x_end).
   * A horizontal line will wrap if (y_start > y_end).
   * @param lineGeometry        The rect representing line geometry
   * @constructor
   */
  public static CreateFromVerticalLine(
    lineGeometry: GeckoComponentRect
  ): GeckoComponentPath {
    const y_start = lineGeometry.y.start.num
    const y_end = lineGeometry.y.end.num
    const x = lineGeometry.x.start.num
    return GeckoComponentPath.CreateFromValues(
      lineGeometry.componentID,
      [
        { x: x, y: y_start },
        { x: x, y: y_end },
      ],
      lineGeometry.x.unit,
      lineGeometry.y.unit,
      [LineDirectionX.None],
      [LineDirectionY.Up]
    )
  }

  /**
   * A factory method for creating a circular GeckoComponentPath.
   * The circle is defined by center (centerX, centerY) and radius, with units provided.
   * The path is closed by repeating the first point at the end.
   */
  public static CreateFromCircle(
    componentID: string,
    centerX: number,
    centerY: number,
    radius: number,
    xUnit: GeckoMeasurementUnit,
    yUnit: GeckoMeasurementUnit,
    segments: number = 64
  ): GeckoComponentPath {
    const coords: { x: number; y: number }[] = []
    for (let i = 0; i <= segments; i++) {
      const t = (i / segments) * 2 * Math.PI
      coords.push({
        x: centerX + radius * Math.cos(t),
        y: centerY + radius * Math.sin(t),
      })
    }
    return GeckoComponentPath.CreateFromValues(
      componentID,
      coords,
      xUnit,
      yUnit
    )
  }

  /**
   * A factory method for creating a GeckoComponentPath from an array of
   * GeckoComponentCoords, converting to the first coords' measurement units
   * @param coordsArray   An array of GeckoComponentCoords
   * @constructor
   */
  public static CreateFromCoords(
    coordsArray: GeckoComponentCoords[]
  ): GeckoComponentPath {
    return new GeckoComponentPath(coordsArray)
  }

  //----------------------------------------------------------------------------

  /**
   * A method which returns a GeckoComponentPath object with new measurement units.
   * Note that if no conversion is necessary, this instance will be returned.
   * @param xUnit         The new measurement unit of the x coordinate
   * @param yUnit         The new measurement unit of the y coordinate
   */
  public convertTo(
    xUnit: GeckoMeasurementUnit | undefined,
    yUnit: GeckoMeasurementUnit | undefined
  ): GeckoComponentPath {
    if ((this.x.unit === xUnit || !xUnit) && (this.y.unit === yUnit || !yUnit))
      return this
    return GeckoComponentPath.CreateFromCoords(
      this.points.map((point) => point.convertTo(xUnit, yUnit))
    )
  }

  public toJSON(): Record<string, unknown> {
    return {
      points: this.points.map((point) => ({
        x: point.x.num,
        y: point.y.num,
      })),
      x_measurement_unit: this.x.unit.id,
      y_measurement_unit: this.y.unit.id,
      component_id: this.componentID,
    }
  }

  /** Returns a simple string representing this bin rectangle */
  public toString(): string {
    return (
      `${this.componentID} ` +
      this.points.map((point) => `(${point.x}, ${point.y})`).join(', ')
    )
  }
}

/**
 * Returns line x direction, based on start and end x and whether we know it wraps
 * @param sx start x
 * @param ex end x
 * @param lineWraps line segment wraps
 * @param wrapOnEquals override to wrap sx === ex on a line that wraps; always outputs Left
 * @returns
 */
export function determineXDirection(
  sx: number,
  ex: number,
  lineWraps: boolean = false,
  wrapOnEquals: boolean = false
) {
  return sx === ex && !(wrapOnEquals && lineWraps)
    ? LineDirectionX.None
    : (ex > sx && !lineWraps) || (ex < sx && lineWraps)
      ? LineDirectionX.Right
      : LineDirectionX.Left
}

/**
 * Returns line y direction, based on start and end y and whether we know it wraps
 * @param sy start y
 * @param ey end y
 * @param lineWraps line segment wraps
 * @param wrapOnEquals override to wrap sy === ey on a line that wraps; always outputs Down
 * @returns
 */
export function determineYDirection(
  sy: number,
  ey: number,
  lineWraps: boolean = false,
  wrapOnEquals: boolean = false
) {
  return sy === ey && !(wrapOnEquals && lineWraps)
    ? LineDirectionY.None
    : (ey > sy && !lineWraps) || (ey < sy && lineWraps)
      ? LineDirectionY.Up
      : LineDirectionY.Down
}
